/**
 * ThreatMetrix (TMX) Device Profiling
 *
 * Pure, framework-agnostic helpers for resolving TMX profiling configuration
 * from per-app `NEXT_PUBLIC_*` env vars and for building the profiling URLs.
 *
 * This module deliberately contains NO React and NO DOM access — the functions
 * here are pure and unit-testable in isolation. The actual DOM trigger (which
 * injects the profiling iframe / invokes the loader-defined global) lives in a
 * separate component created later as part of CST-25471.
 *
 * The loader library itself is vendored at `/tmx/tmx-loader.js` (served from
 * each app's `public/`) and defines the obfuscated global
 * `window.asset81hasf`. Profiling is started by calling
 * `asset81hasf.profile(domain, orgId, sessionId, pageId)` — the `domain`,
 * `orgId` and `pageId` resolved here are the arguments to that call (and
 * `domain`/`orgId`/`pageId` also feed the <noscript> beacon iframe URL).
 *
 * TMX profiling silently collects device intelligence to feed the fraud/threat
 * checks orchestrated downstream by LighteningService. It must be skipped
 * entirely unless it is both enabled and fully configured — see
 * `isTmxProfilingConfigured()`.
 */

/**
 * Resolved TMX profiling configuration.
 *
 * `pageId` always has a value because `resolveTmxProfilingConfig()` applies a
 * default of `'1'`.
 */
export interface TmxProfilingConfig {
  enabled: boolean;
  orgId: string;
  domain: string;
  pageId: string;
}

/** The subset of environment values `resolveTmxProfilingConfig` reads. `process.env` and a plain object of the NEXT_PUBLIC_TMX_* keys both satisfy this. */
export type TmxProfilingEnv = Record<string, string | undefined>;

/**
 * Reads a configured string value, trimming surrounding whitespace, and falls
 * back to the supplied default when the value is missing or blank. This keeps
 * an explicitly-empty env var from overriding a safe default.
 */
const readTrimmed = (value: string | undefined, fallback: string): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
};

/**
 * Reads TMX profiling configuration from the provided environment (defaults to
 * `process.env`). Never throws — missing values fall back to safe defaults so
 * callers can rely on `isTmxProfilingConfigured()` as the single guard.
 *
 * @param env The environment to read from. Defaults to `process.env`.
 * @returns The resolved TMX profiling configuration.
 */
export function resolveTmxProfilingConfig(
  env: TmxProfilingEnv = process.env,
): TmxProfilingConfig {
  return {
    // Compare trimmed so a value like ' true ' still enables profiling.
    enabled: env.NEXT_PUBLIC_TMX_PROFILING_ENABLED?.trim() === 'true',
    orgId: readTrimmed(env.NEXT_PUBLIC_TMX_ORG_ID, ''),
    domain: readTrimmed(env.NEXT_PUBLIC_TMX_PROFILING_DOMAIN, ''),
    pageId: readTrimmed(env.NEXT_PUBLIC_TMX_PAGE_ID, '1'),
  };
}

/**
 * The single guard the trigger uses to decide whether to profile.
 *
 * Returns true only when profiling is enabled AND both `orgId` and `domain`
 * are non-empty once trimmed. `pageId` is not checked because it always has a
 * default. When this returns false, profiling must be skipped entirely.
 *
 * @param config The resolved TMX profiling configuration.
 * @returns Whether TMX profiling is enabled and fully configured.
 */
export function isTmxProfilingConfigured(config: TmxProfilingConfig): boolean {
  return (
    config.enabled === true &&
    config.orgId.trim().length > 0 &&
    config.domain.trim().length > 0
  );
}

/**
 * Builds the TMX profiling iframe URL for a given session.
 *
 * The URL shape is:
 * `https://{domain}/tags?org_id={orgId}&session_id={sessionId}&pageid={pageId}`
 *
 * `orgId`, `sessionId` and `pageId` are passed through `encodeURIComponent`.
 *
 * NB: there is deliberately NO space after `tags?`. The legacy Sitecore markup
 * had a bug — `"/tags? org_id="` with a stray space — which produced a
 * malformed URL; that mistake is not reproduced here.
 *
 * @param config The resolved TMX profiling configuration.
 * @param sessionId The profiling session identifier.
 * @returns The fully-formed profiling iframe URL.
 */
export function buildTmxProfilingIframeUrl(
  config: TmxProfilingConfig,
  sessionId: string,
): string {
  const orgId = encodeURIComponent(config.orgId);
  const session = encodeURIComponent(sessionId);
  const pageId = encodeURIComponent(config.pageId);

  // No space after `tags?` — see JSDoc note about the legacy Sitecore bug.
  return `https://${config.domain}/tags?org_id=${orgId}&session_id=${session}&pageid=${pageId}`;
}

/**
 * Shape of the loader-defined profiling object so the trigger component can
 * call it without resorting to `any`.
 */
export interface TmxProfilingGlobal {
  profile: (
    domain: string,
    orgId: string,
    sessionId: string,
    pageId: string,
  ) => void;
}

declare global {
  interface Window {
    /**
     * `asset81hasf` is the obfuscated global defined by the per-org TMX loader
     * (tags.js). Its name is org-specific, so treat this as the ROM/POL1
     * binding used by the current loader rather than a stable public name.
     */
    asset81hasf?: TmxProfilingGlobal;
  }
}

// Keep this file a module even with the `declare global` augmentation above.
export {};
